<template>
  <v-list-item-subtitle class="mx-0 px-0">
    <v-row>
      <v-col cols="12" class="d-flex">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn small icon color="red" dark v-on="on" @click="hangup">
              <v-icon small>mdi-phone-hangup</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("sipua.menu.hangup") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              :color="iconColor"
              v-on="on"
              @click="$emit('active-call-dialpad', call.callid)"
              :disabled="call.isOnHold"
            >
              <v-icon small>mdi-dialpad</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("sipua.menu.dialpad") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              :color="call.isOnHold ? 'green' : iconColor"
              v-on="on"
              @click="onhold"
              :disabled="disabledOnHold"
            >
              <v-icon small
                >mdi-{{ call.isOnHold ? "pause-circle" : "pause" }}</v-icon
              >
            </v-btn>
          </template>
          <span>{{
            $t("sipua.menu." + (call.isOnHold ? "unhold" : "hold"))
          }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              :color="call.isMuted ? 'red' : iconColor"
              v-on="on"
              @click="mute"
              :disabled="disabledMute"
            >
              <v-icon small
                >mdi-microphone{{ call.isMuted ? "-off" : "" }}</v-icon
              >
            </v-btn>
          </template>
          <span>{{
            $t("sipua.menu." + (call.isMuted ? "unmute" : "mute"))
          }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              :color="iconColor"
              :disabled="call.isOnHold"
              @click="showVol = !showVol"
              v-on="on"
            >
              <v-icon small>mdi-volume-high</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("sipua.menu.volume") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              :color="iconColor"
              @click="park"
              :disabled="disabledParking"
              v-on="on"
            >
              <v-icon small>mdi-parking</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("sipua.menu.parking") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              :color="iconColor"
              v-on="on"
              :loading="loading3wc"
              @click="threeWayConfStart"
              :disabled="disabled3WConf"
            >
              <v-icon small>mdi-call-split</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("sipua.menu.conf3w") }}</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              :color="iconColor"
              v-on="on"
              :disabled="disabledXfer"
              @click="toggleXfer"
            >
              <v-icon small>mdi-phone-forward</v-icon>
            </v-btn>
          </template>
          <span>{{ $t("sipua.menu.xfer") }}</span>
        </v-tooltip>

        <v-tooltip bottom v-if="canCallRecord">
          <template v-slot:activator="{ on }">
            <v-btn
              small
              icon
              color="red"
              v-on="on"
              @click="$emit('active-call-recorder')"
              :disabled="disabledRecording"
            >
              <v-icon color="red">{{ recIcon }}</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(recI18nKey) }}</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <v-expand-transition>
      <v-row v-if="showVol" justify="center" dense class="py-0">
        <v-col md="12">
          <v-slider
            v-model="volume"
            dense
            append-icon="mdi-volume-high"
            prepend-icon="mdi-volume-medium"
          >
          </v-slider>
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-expand-transition>
      <v-row v-if="showXfer" justify="center" dense class="py-0">
        <v-col md="10" sm="8">
          <v-combobox
            ref="xferCombobox"
            v-model="callXfer"
            :label="$t('sipua.xfer.label')"
            :placeholder="$t('sipua.xfer.placeholder')"
            :items="xferList"
            :search-input.sync="xferSearch"
            :error-messages="xferError"
            dense
            clearable
            hide-no-data
            @change="xferError = ''"
            @keydown="xferError = ''"
          >
            <template v-slot:item="{ item }">
              <v-list-item-avatar>
                <v-img
                  v-if="item.hasImage"
                  :src="item.imageUrl"
                  :alt="item.name"
                ></v-img>
                <v-img
                  v-else
                  src="/default_picture_profile.png"
                  :alt="item.name"
                ></v-img>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title v-html="item.name" />
                <v-list-item-subtitle>
                  {{ item.label }}:
                  {{ item.phoneNumber | formatPhoneNum(countryCode) }}
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-combobox>
        </v-col>
        <v-col md="2" class="pb-0 mb-0">
          <v-btn icon dense color="primary" @click="xferClick">
            <v-icon dense>mdi-forward</v-icon>
          </v-btn>
        </v-col>
        <v-col md="12" class="pb-0 mb-0">
          <v-radio-group dense row v-model="xferType" class="py-0 my-0">
            <v-radio label="blind" value="BLIND"></v-radio>
            <v-radio label="attended" value="ATTEND"></v-radio>
          </v-radio-group>
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-expand-transition>
      <v-row v-if="show3wc" justify="center" dense class="pt-0 pb-0">
        <v-col md="10" sm="8">
          <v-text-field
            @keyup.enter="threeWayConfInvite"
            v-model="invitee3wc"
            dense
            :rules="[(val) => !!val || '']"
            :label="$t('sipua.menu.label3wc')"
          >
          </v-text-field>
        </v-col>
        <v-col md="2">
          <v-btn icon small dark color="primary" @click="threeWayConfInvite">
            <v-icon small>mdi-account-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </v-expand-transition>
  </v-list-item-subtitle>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as RMQClient from '@/rmqclient'
import events from 'Store/events'
import SIPUA from '@/sipua/SIPUA'

const context = 'confbridge-three-way'

export default {
  name: 'active-calls-menu',
  props: {
    call: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      iconColor: 'primary',
      showVol: false,
      showXfer: false,
      show3wc: false,
      loading3wc: false,
      exten3wc: 0,
      active3wc: false,
      invitee3wc: '',
      xferType: 'BLIND',
      xferSearch: '',
      xferError: '',
      callXfer: ''
    }
  },
  computed: {
    ...mapGetters({
      audioVol: 'sipua/audioVol',
      realm: 'sipua/realm',
      callerId: 'sipua/callerId',
      canCallRecord: 'permissions/canCallRecord',
      phoneNumbers: 'contacts/phoneNumbers/phoneNumbers',
      countryCode: 'countryCode'
    }),
    disabledRecording: function () {
      return this.call.isBargein || this.call.isOnHold || this.recReady
    },
    disabledXfer: function () {
      return this.call.isBargein
    },
    disabled3WConf: function () {
      return this.call.isBargein || this.call.isOnHold
    },
    disabledParking: function () {
      return this.call.isBargein
    },
    disabledOnHold: function () {
      return this.call.isBargein
    },
    disabledMute: function () {
      if (this.call.isOnHold) {
        return true
      }
      return this.call.isBargein && this.call.bargein.mode === 'monitor'
    },
    volume: {
      get: function () {
        return this.audioVol
      },
      set: function (val) {
        this.setVolume(val)
      }
    },
    recReady: function () {
      return !!this.call.channel && !!this.call.amisrvid
    },
    recIcon: function () {
      return this.call.recording ? 'mdi-stop' : 'mdi-record-rec'
    },
    recI18nKey: function () {
      return this.call.recording ? 'sipua.menu.recStop' : 'sipua.menu.recStart'
    },
    xferList: function () {
      return this.phoneNumbers
        .map((num) => ({
          text: `${num.name} <${num.phoneNumber}>`,
          phoneNumber: num.phoneNumber,
          name: num.name,
          label: num.label,
          hasImage: num.hasImage,
          imageUrl: num.imageUrl
        }))
        .sort((a, b) => a.name.localeCompare(b.name))
    }
  },
  methods: {
    ...mapActions({
      setVolume: 'sipua/setAudioVolume',
      hangupCall: 'sipua/hangupCall',
      holdCall: 'sipua/holdCall',
      xferCall: 'sipua/xferCall',
      parkCall: 'sipua/parkCall',
      attendedXferCall: 'sipua/callAttendedXfer',
      muteCall: 'sipua/muteCall'
    }),
    toggleXfer: function () {
      this.showXfer = !this.showXfer
      this.$emit('xfer-toggle')
    },
    xferClick: function () {
      this.$refs.xferCombobox.blur()
      this.$nextTick(() => {
        const xferNum = this.callXfer
          ? this.callXfer.phoneNumber || this.callXfer
          : ''
        if (!SIPUA.isValidNum(xferNum)) {
          this.xferError = this.$t('sipua.hint.missNum')
          return
        }
        // attended transfer
        if (this.xferType === 'ATTEND') {
          this.toggleXfer()
          this.attendedXferCall({
            num: xferNum,
            cid: this.call.callid,
            user: this.call.number
          })
          return
        }
        // blind transfer
        this.xferCall({ callid: this.call.callid, number: xferNum })
      })
    },
    hangup: function () {
      this.hangupCall(this.call.callid)
    },
    onhold: function () {
      this.holdCall({ callid: this.call.callid, enabled: !this.call.isOnHold })
    },
    mute: function () {
      this.muteCall({ callid: this.call.callid, enabled: !this.call.isMuted })
    },
    park: function () {
      this.parkCall(this.call.callid)
    },
    threeWayConfStart: function () {
      if (this.active3wc) {
        // 3-way conference already active
        this.show3wc = !this.show3wc
        return
      }
      if (this.call.bridgePeer) {
        this.threeWayConfReidrect()
        this.dbg('Bridge peer channel id is found. Redirect channels.')
        return
      }
      // get bridge peer channel id
      this.dbg(
        'Trying to get bridge peer channel id from AMI before redirect.'
      )
      this.loading3wc = true
      RMQClient.sendCmd({
        action: 'bridgeinfo',
        bridgeuniqueid: this.call.bridgeid,
        srvid: this.call.amisrvid
      })

      new Promise((resolve, reject) => {
        let tick = 0
        // wait for response from AMI
        const timerID = setInterval(() => {
          if (this.call.bridgePeer) {
            clearInterval(timerID)
            resolve()
          }
          if (tick >= 10) {
            clearInterval(timerID)
            reject(new Error('Failed to setup three-Way conference.'))
          }
          tick++
        }, 500)
      })
        .then(() => {
          this.threeWayConfReidrect()
        })
        .catch((err) => {
          events.pubError(err.message)
        })
        .finally(() => {
          this.loading3wc = false
        })
    },
    threeWayConfReidrect: function () {
      // random 8 num extension
      const exten = Math.floor(Math.random() * Math.pow(10, 8))
      this.exten3wc = `3WC-${exten.toString()}`
      const cmdRedir = {
        action: 'redirect',
        srvid: this.call.amisrvid,
        channel: this.call.channel,
        extrachannel: this.call.bridgePeer,
        exten: this.exten3wc,
        extraexten: this.exten3wc,
        context: context,
        extracontext: context,
        priority: '1',
        extrapriority: '1'
      }
      RMQClient.sendCmd(cmdRedir)
      this.active3wc = true
      this.dbg('Redirect command is sent: %o', cmdRedir)
      this.show3wc = true
    },
    threeWayConfInvite: function () {
      if (!this.invitee3wc.trim()) {
        return
      }
      const cmd = {
        action: 'Originate',
        channel: `Local/${this.invitee3wc}@confbridge-invite`,
        exten: this.exten3wc,
        priority: '1',
        context: context,
        srvid: this.call.amisrvid,
        callerid: this.callerId,
        variable: {
          realm: this.realm
        }
      }
      RMQClient.sendCmd(cmd)
      this.invitee3wc = ''
      this.show3wc = false
    }
  }
}
</script>

<style scoped></style>
